import { Button, Empty, Spin } from 'antd';
import { useState } from 'react';
import { generatePath, Link, useParams } from 'react-router-dom';

import { SectionTitle } from '@/components';
import { ROUTES_PATH } from '@/constants/routes.constants';
import { MATERIAL_PAGE_SIZE } from '@/constants/table.constants';
import { useUserInformation } from '@/hooks/use-user-information';

import { useDrinkMaterial, usePrinterAndFixtures } from '../queries/materials';
import { DrinkMaterialsTable } from './tables/drink-materials-table';
import { PrinterAndFixturesTable } from './tables/printer-and-fixtures-table';

const emptyText = (
  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}>
    1店舗目が承認されると注文ができます
  </Empty>
);

interface OrganizationShopsProps {
  activeKey: string;
}

export function OrganizationMaterials({
  activeKey,
}: Readonly<OrganizationShopsProps>) {
  const { id } = useParams();
  const { organizationId, isChainRole } = useUserInformation();
  const [currentDrinkMaterialPage, setCurrentDrinkMaterialPage] = useState(1);
  const [currentPrinterAndFixturesPage, setCurrentPrinterAndFixturesPage] =
    useState(1);
  const currentOrganizationId = id ?? organizationId!;

  const {
    data: drinkMaterial,
    isLoading: isDrinkMaterialLoading,
    isFetching: isDrinkMaterialFetching,
  } = useDrinkMaterial(
    currentOrganizationId,
    { page: currentDrinkMaterialPage, per_page: MATERIAL_PAGE_SIZE },
    { enabled: activeKey === 'order' },
  );
  const {
    data: printerAndFixtures,
    isLoading: isPrinterAndFixturesLoading,
    isFetching: isPrinterAndFixturesFetching,
  } = usePrinterAndFixtures(
    currentOrganizationId,
    { page: currentPrinterAndFixturesPage, per_page: MATERIAL_PAGE_SIZE },
    { enabled: activeKey === 'order' && !isChainRole },
  );
  const isDisableButtonOrderMaterial =
    !drinkMaterial?.is_store_registration_approved;
  const isDisableButtonOrderFixture =
    !printerAndFixtures?.is_store_registration_approved;

  if (isDrinkMaterialLoading || isPrinterAndFixturesLoading)
    return <Spin size="small" />;

  return (
    <>
      <SectionTitle
        title="ドリンク・資材注文"
        rightContent={
          <Link
            to={
              id
                ? generatePath(ROUTES_PATH.organizationDrinkMaterialsNew, {
                    id,
                  })
                : ROUTES_PATH.myOrganizationDrinkMaterialsNew
            }
          >
            <Button type="primary" disabled={isDisableButtonOrderMaterial}>
              注文
            </Button>
          </Link>
        }
      />
      <DrinkMaterialsTable
        dataSource={drinkMaterial?.drink_and_material_orders}
        {...(isDisableButtonOrderMaterial && { locale: { emptyText } })}
        loading={isDrinkMaterialFetching}
        pagination={{
          total: drinkMaterial?.total_records,
          onChange(page) {
            setCurrentDrinkMaterialPage(page);
          },
        }}
      />
      {!isChainRole && (
        <>
          <SectionTitle
            title="プリンタ・什器申請"
            className="mt-32"
            rightContent={
              <Link
                to={generatePath(
                  ROUTES_PATH.organizationPrinterAndFixturesNew,
                  { id: id! },
                )}
              >
                <Button type="primary" disabled={isDisableButtonOrderFixture}>
                  申請
                </Button>
              </Link>
            }
          />
          <PrinterAndFixturesTable
            dataSource={printerAndFixtures?.printer_and_fixtures}
            loading={isPrinterAndFixturesFetching}
            {...(isDisableButtonOrderFixture && { locale: { emptyText } })}
            pagination={{
              total: printerAndFixtures?.total_records,
              onChange(page) {
                setCurrentPrinterAndFixturesPage(page);
              },
            }}
          />
        </>
      )}
    </>
  );
}
